import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = { url: String };

  connect() {
    this.setupIntersectionObserver();
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  setupIntersectionObserver() {
    if (!this.urlValue) return;

    // Check for IntersectionObserver support
    if (!window.IntersectionObserver) {
      // Fallback: load immediately if no IntersectionObserver support
      this.loadBackgroundImage();
      return;
    }

    // Use intersection observer for better performance
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.loadBackgroundImage();
            this.observer.disconnect();
          }
        });
      },
      {
        rootMargin: "50px", // Start loading when element is 50px from viewport
      },
    );

    this.observer.observe(this.element);
  }

  loadBackgroundImage() {
    if (!this.urlValue) return;

    // Create a new image to preload with performance optimizations
    const img = new Image();

    // Set loading priority for better performance
    img.loading = "eager";
    img.fetchPriority = "high";

    // Add performance hints
    img.decoding = "async";

    img.onload = () => {
      // Use requestAnimationFrame for smooth rendering
      requestAnimationFrame(() => {
        // Once the image is loaded, apply it as background
        this.element.style.backgroundImage = `url(${this.urlValue})`;
        this.element.style.backgroundSize = "cover";
        this.element.style.backgroundPosition = "center center";
        this.element.style.backgroundRepeat = "no-repeat";

        // Add a class for additional styling if needed
        this.element.classList.add("bg-loaded");

        // Dispatch custom event for analytics or other listeners
        this.element.dispatchEvent(
          new CustomEvent("bg-loaded", {
            detail: { url: this.urlValue },
          }),
        );
      });
    };

    img.onerror = () => {
      console.warn("Failed to load hero background image:", this.urlValue);
      // Keep the solid background color as fallback
      this.element.classList.add("bg-error");
    };

    // Start loading the image
    img.src = this.urlValue;
  }
}
